import { GoodsCarousel as UIKitGoodsCarousel } from '@banhala/ui-kit';
import type { GoodsCarouselProps } from './types';

import { bridge, isNotNil, useNavigator } from '../../../../utils';

import { logOnGoodsPressItem } from './log/logOnPressItem';
import type { GoodsCoreModel, IScreensLogging } from '../../../../api/types';
import { logOnPressLike } from './log/logOnPressLike';
import { useAddLikeGoods } from '../../../../hooks';
import { useLogDisplayGoods } from './log/useLogDisplayGoods';
import { getGoodsCarouselData } from './goodsCarouselDataConverter';
import { useEffect } from 'react';
import { useRemoveLikeGoods } from '../../../../queries';

/**
 *
 * @example
 * <AtomicRenderer data={atomicRendererDataConverter(mockHeadlineSmallOneLineData)} />
 * // mockHeadlineSmallOneLineData를 사용하면 GoodsCarousel까지 확인할 수 있습니다.
 */
export const GoodsCarousel = ({ entity, likeMap, setLikeMap }: GoodsCarouselProps) => {
  const goodsData = getGoodsCarouselData({ entity });
  const { navigator } = useNavigator();

 useEffect(() => {
  setLikeMap(prevState => {
  return {
    ...prevState, 
    ...entity?.reduce<Record<string, boolean>>((acc, cur) => {
      acc[cur.item.sno] = cur.item.like?.is_liked ?? false;
      return acc;
    }, {}),
  }
  });
 }, [entity, setLikeMap])
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { logDisplayGoods } = useLogDisplayGoods();

  const { addLikeGoods } = useAddLikeGoods({
    listParams: entity?.[0]?.logging.analytics?.LIST_ID as string | undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
     screen: (entity?.[0]?.logging.analytics?.SCREEN as string | undefined) ?? "",
  });

  if (!entity) {
    return null;
  }

  const handlePressItem = async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    logOnGoodsPressItem({ logging });
    navigator.moveToGoods({
      sno: item.sno,
      firstPageRendering: item.first_page_rendering ?? null,
      loggingAnalytics: logging.analytics,
    });
  };

  const handlePressLike = async ({
    logging,
    item,
  }: {
    logging: IScreensLogging;
    item: GoodsCoreModel;
  }) => {
    const currentLike = likeMap[item.sno] ?? false;
    logOnPressLike({ logging, currentLike });
    if (currentLike) {
      await mutateRemoveLikeGoods({
        sno: item.sno,
        list_params_referrer: logging.analytics?.LIST_ID as string | undefined,
      });
      bridge.likeGoods({
        goods_sno: item.sno,
        is_liked: false,
      });
      setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
      return;
    }
    addLikeGoods(item.sno, logging.analytics, logging.ably_only_log);
  };

  return (
    <UIKitGoodsCarousel
      likeMap={likeMap}
      onPressLike={handlePressLike}
      size={'medium'}
      showRanking={entity.some(item => isNotNil(item.render?.data.ranking))}
      list={goodsData}
      onPressItem={async ({ item, logging }) => {
        handlePressItem({ logging, item });
      }}
      keyExtractor={(item, index) => (item.sno ?? index).toString()}
      onViewableItemsChanged={logDisplayGoods}
    />
  );
};
